import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Clock, CheckCircle2, PauseCircle, CalendarPlus, Layers, Filter, X, ChevronDown, ChevronUp, Edit2, Check, Loader2, Save, MessageSquare, Link2, FileSpreadsheet, FileText, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { ZohoCustomerLink } from '../../components/ZohoLinks';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function exportToExcel(rows, columns, filename) {
  const data = rows.map(r => { const obj = {}; columns.forEach(c => { obj[c.label] = c.render ? c.render(r) : r[c.key]; }); return obj; });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
}

function exportToPdf(rows, columns, title) {
  const doc = new jsPDF({ orientation: columns.length > 6 ? 'landscape' : 'portrait' });
  doc.setFontSize(14); doc.text(title, 14, 18);
  doc.setFontSize(8); doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, 14, 24);
  autoTable(doc, { startY: 28, head: [columns.map(c => c.label)], body: rows.map(r => columns.map(c => c.render ? c.render(r) : (r[c.key] ?? '-'))), styles: { fontSize: 7 }, headStyles: { fillColor: [109, 40, 217] } });
  doc.save(`${title.replace(/\s/g, '_')}.pdf`);
}

// ─── Trainer MultiSelect ─────────────────────────────────────
function TrainerFilter({ trainers, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const toggle = (val) => { const s = new Set(selected); if (s.has(val)) s.delete(val); else s.add(val); onChange(Array.from(s)); };
  const label = selected.length === 0 ? 'All Trainers' : `${selected.length} trainer${selected.length > 1 ? 's' : ''}`;
  const filtered = search ? trainers.filter(t => t.name.toLowerCase().includes(search.toLowerCase())) : trainers;
  return (
    <div className="relative" data-testid="overview-trainer-filter">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 h-9 px-3 border rounded-sm text-xs text-slate-600 bg-white hover:bg-slate-50 min-w-[160px]">
        <Filter className="w-3 h-3 text-slate-400" />{label}
        {selected.length > 0 && <X className="w-3 h-3 ml-auto text-slate-400 hover:text-red-500" onClick={e => { e.stopPropagation(); onChange([]); }} />}
      </button>
      {open && (<>
        <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearch(''); }} />
        <div className="absolute z-50 w-64 mt-1 bg-white border rounded-md shadow-lg max-h-72 overflow-hidden">
          <div className="p-1.5 border-b"><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search trainers..." className="w-full h-7 px-2 text-xs border rounded-sm outline-none focus:border-violet-300" autoFocus data-testid="overview-trainer-search" /></div>
          <div className="overflow-y-auto max-h-56">
            {filtered.map(t => (
              <label key={t.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-violet-50 cursor-pointer text-xs">
                <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggle(t.id)} className="rounded border-slate-300" />{t.name}
              </label>
            ))}
          </div>
        </div>
      </>)}
    </div>
  );
}

// ─── Editable Cells ──────────────────────────────────────────
function EditableCell({ value, field, batchId, studentId, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const ref = useRef(null);
  useEffect(() => { setVal(value || ''); }, [value]);
  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);
  const save = async () => { setEditing(false); if (val !== (value || '')) await onSave(batchId, studentId, { [field]: val }); };
  if (!editing) return (
    <div className="cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded min-h-[24px] text-xs text-slate-600 group flex items-center gap-1" onClick={() => setEditing(true)}>
      {val || <span className="text-slate-300 italic">-</span>}<Edit2 className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100" />
    </div>
  );
  return <Input ref={ref} value={val} onChange={e => setVal(e.target.value)} onBlur={save} onKeyDown={e => e.key === 'Enter' && save()} className="h-6 text-xs px-1 w-full min-w-[60px]" />;
}

function AttendanceCell({ value, batchId, studentId, onSave }) {
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  const handleChange = async (v) => { setVal(v); await onSave(batchId, studentId, { attendance: v }); };
  const colors = { Regular: 'bg-emerald-50 text-emerald-700 border-emerald-200', Absenteeism: 'bg-red-50 text-red-700 border-red-200' };
  return (
    <Select value={val || '__none__'} onValueChange={v => handleChange(v === '__none__' ? '' : v)}>
      <SelectTrigger className={`h-6 text-[10px] rounded-sm min-w-[90px] border ${colors[val] || 'border-slate-200'}`}><SelectValue placeholder="-" /></SelectTrigger>
      <SelectContent><SelectItem value="__none__">-</SelectItem><SelectItem value="Regular">Regular</SelectItem><SelectItem value="Absenteeism">Absenteeism</SelectItem></SelectContent>
    </Select>
  );
}

function PlacementCheckbox({ value, batchId, studentId, onSave }) {
  const [checked, setChecked] = useState(!!value);
  useEffect(() => { setChecked(!!value); }, [value]);
  const handleToggle = async () => { const v = !checked; setChecked(v); await onSave(batchId, studentId, { ready_for_placement: v }); };
  return (
    <button onClick={handleToggle} className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors ${checked ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-300 hover:border-slate-400'}`}>
      {checked && <Check className="w-3.5 h-3.5 text-white" />}
    </button>
  );
}

// ─── Batch Report Popover ────────────────────────────────────
function BatchReportDialog({ batch, open, onClose }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batchComment, setBatchComment] = useState(batch?.comment || '');
  const [whatsappLink, setWhatsappLink] = useState(batch?.whatsapp_link || '');
  const [savingComment, setSavingComment] = useState(false);

  useEffect(() => {
    if (!open || !batch) return;
    setLoading(true);
    setBatchComment(batch.comment || '');
    setWhatsappLink(batch.whatsapp_link || '');
    api.get(`/training/reports/trainer-detail/${batch.batch_id}/students`)
      .then(r => setStudents((r.data.students || []).map((s, i) => ({ ...s, sno: i + 1 }))))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [open, batch]);

  const handleSaveRemark = async (batchId, studentId, data) => {
    try { await api.post(`/training/reports/trainer-detail/${batchId}/students/${studentId}/remarks`, data); } catch { toast.error('Save failed'); }
  };

  const updateStudent = (studentId, field, val) => {
    setStudents(prev => prev.map(s => s.student_id === studentId ? { ...s, [field]: val } : s));
  };

  const saveAndUpdate = async (batchId, studentId, data) => {
    await handleSaveRemark(batchId, studentId, data);
    const field = Object.keys(data)[0];
    updateStudent(studentId, field, data[field]);
  };

  const handleSaveComment = async () => {
    setSavingComment(true);
    try {
      await api.post(`/training/reports/trainer-detail/${batch.batch_id}/batch-comment`, { comment: batchComment, whatsapp_link: whatsappLink });
      toast.success('Saved');
    } catch { toast.error('Failed'); }
    setSavingComment(false);
  };

  const commentChanged = batchComment !== (batch?.comment || '') || whatsappLink !== (batch?.whatsapp_link || '');

  const exportCols = [
    { key: 'sno', label: '#' }, { key: 'enrollment_no', label: 'Enrollment' }, { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' }, { key: 'email', label: 'Email' }, { key: 'counsellor', label: 'Counsellor' },
    { key: 'sector', label: 'Sector' }, { key: 'pdp', label: 'PDP' }, { key: 'mock_interview', label: 'Mock Int.' },
    { key: 'attendance', label: 'Attendance' }, { key: 'ready_for_placement', label: 'Placement Ready', render: s => s.ready_for_placement ? 'Yes' : 'No' },
  ];

  if (!batch) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="batch-report-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <span className="text-violet-600 font-bold">#{batch.batch_number}</span>
            <span>{batch.course_name}</span>
            <Badge variant="outline" className="text-[9px] ml-2">{batch.trainer_name}</Badge>
            <Badge variant="outline" className={`text-[9px] ${batch.status === 'Running' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{batch.status}</Badge>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-violet-500 mx-auto" /></div>
        ) : (<>
          {/* Export */}
          <div className="flex justify-end gap-2 mb-2">
            <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => exportToExcel(students, exportCols, `Batch_${batch.batch_number}`)}><FileSpreadsheet className="w-3 h-3 mr-1" />Excel</Button>
            <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => exportToPdf(students, exportCols, `Batch #${batch.batch_number} - ${batch.course_name}`)}><FileText className="w-3 h-3 mr-1" />PDF</Button>
          </div>

          {/* Students Table */}
          <div className="overflow-x-auto border rounded-sm">
            <Table>
              <TableHeader><TableRow className="bg-cyan-50/50">
                <TableHead className="text-[9px] uppercase w-6">#</TableHead>
                <TableHead className="text-[9px] uppercase">Enroll</TableHead>
                <TableHead className="text-[9px] uppercase">Name</TableHead>
                <TableHead className="text-[9px] uppercase">Phone</TableHead>
                <TableHead className="text-[9px] uppercase">Email</TableHead>
                <TableHead className="text-[9px] uppercase">Counsellor</TableHead>
                <TableHead className="text-[9px] uppercase">Sector</TableHead>
                <TableHead className="text-[9px] uppercase">PDP</TableHead>
                <TableHead className="text-[9px] uppercase">Mock Int.</TableHead>
                <TableHead className="text-[9px] uppercase">Attendance</TableHead>
                <TableHead className="text-[9px] uppercase">Placement</TableHead>
                <TableHead className="text-[9px] uppercase text-right">Dues</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {students.length === 0 ? (
                  <TableRow><TableCell colSpan={12} className="text-center text-xs text-slate-400 py-6">No students</TableCell></TableRow>
                ) : students.map(s => (
                  <TableRow key={s.student_id} className="hover:bg-slate-50/50">
                    <TableCell className="text-[10px] text-slate-400">{s.sno}</TableCell>
                    <TableCell className="text-xs"><ZohoCustomerLink enrollmentNo={s.enrollment_no} zohoCustomerId={s.zoho_customer_id} /></TableCell>
                    <TableCell className="text-xs font-medium">{s.name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{s.phone}</TableCell>
                    <TableCell className="text-[10px] text-blue-600 max-w-[100px] truncate">{s.email}</TableCell>
                    <TableCell className="text-[10px] text-slate-500">{s.counsellor || '-'}</TableCell>
                    <TableCell><EditableCell value={s.sector} field="sector" batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                    <TableCell><EditableCell value={s.pdp} field="pdp" batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                    <TableCell><EditableCell value={s.mock_interview} field="mock_interview" batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                    <TableCell><AttendanceCell value={s.attendance} batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                    <TableCell className="text-center"><PlacementCheckbox value={s.ready_for_placement} batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                    <TableCell className="text-right">
                      {s.has_overdue ? <Badge variant="outline" className="text-[9px] bg-red-50 text-red-600"><AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Overdue</Badge>
                      : s.has_partially_paid ? <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-600">Partial</Badge>
                      : <span className="text-[9px] text-slate-300">-</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Batch Comment */}
          <div className="border rounded-sm p-3 bg-slate-50/50 space-y-2 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Batch Comment</Label><Textarea value={batchComment} onChange={e => setBatchComment(e.target.value)} className="mt-1 text-xs min-h-[50px]" /></div>
              <div><Label className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1"><Link2 className="w-3 h-3" /> WhatsApp Link</Label><Input value={whatsappLink} onChange={e => setWhatsappLink(e.target.value)} className="mt-1 text-xs h-8" /></div>
            </div>
            {commentChanged && <Button size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700" onClick={handleSaveComment} disabled={savingComment}><Save className="w-3 h-3 mr-1" />{savingComment ? 'Saving...' : 'Save'}</Button>}
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
}

// ─── KPI Card ────────────────────────────────────────────────
const KPI_CONFIG = [
  { key: 'weekday_running', label: 'Weekday Running', icon: Clock, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', activeBg: 'bg-emerald-600 text-white' },
  { key: 'weekend_running', label: 'Weekend Running', icon: Clock, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', activeBg: 'bg-emerald-600 text-white' },
  { key: 'upcoming', label: 'Upcoming', icon: CalendarPlus, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', activeBg: 'bg-blue-600 text-white' },
  { key: 'on_hold', label: 'On Hold', icon: PauseCircle, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', activeBg: 'bg-amber-600 text-white' },
  { key: 'completed', label: 'Completed', icon: CheckCircle2, color: 'text-slate-500', bg: 'bg-slate-50 border-slate-200', activeBg: 'bg-slate-600 text-white' },
];

// ─── Main Page ───────────────────────────────────────────────
export default function TrainerOverviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTrainers, setSelectedTrainers] = useState([]);
  const [activeKPIs, setActiveKPIs] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedTrainers.length > 0) params.append('trainer_ids', selectedTrainers.join(','));
    api.get(`/training/reports/trainer-overview?${params}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [selectedTrainers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleKPI = (key) => {
    setActiveKPIs(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  if (loading && !data) return <div className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>;

  const kpi = data?.kpi || {};

  // Collect batches from all active KPIs
  const visibleBatches = activeKPIs.flatMap(k => data?.[k] || []);

  return (
    <div className="space-y-6" data-testid="trainer-overview-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Trainer Overview</h1>
          <p className="text-sm text-slate-400 mt-0.5">Bird's eye view across all trainers and batches</p>
        </div>
        <TrainerFilter trainers={data?.trainers || []} selected={selectedTrainers} onChange={setSelectedTrainers} />
      </div>

      {/* KPI Cards — clickable */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {KPI_CONFIG.map(cfg => {
          const isActive = activeKPIs.includes(cfg.key);
          const count = kpi[cfg.key] || 0;
          const Icon = cfg.icon;
          return (
            <Card key={cfg.key}
              className={`border p-4 cursor-pointer transition-all duration-200 ${isActive ? cfg.activeBg + ' border-transparent shadow-md scale-[1.02]' : 'hover:border-violet-200 hover:shadow-sm'}`}
              onClick={() => toggleKPI(cfg.key)}
              data-testid={`kpi-${cfg.key}`}
            >
              <div className="flex items-center justify-between mb-1">
                <Icon className={`w-4 h-4 ${isActive ? 'text-white/80' : cfg.color}`} />
                {isActive && <Check className="w-3.5 h-3.5 text-white/80" />}
              </div>
              <p className={`text-2xl font-bold ${isActive ? '' : cfg.color}`}>{count}</p>
              <p className={`text-[10px] uppercase font-semibold tracking-wider mt-0.5 ${isActive ? 'text-white/80' : 'text-slate-400'}`}>{cfg.label}</p>
            </Card>
          );
        })}
      </div>

      {/* Batch Cards — shown only when KPIs are selected */}
      {activeKPIs.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-violet-500" />
            <p className="text-sm font-semibold text-slate-700">{visibleBatches.length} Batch{visibleBatches.length !== 1 ? 'es' : ''}</p>
            <span className="text-xs text-slate-400">({activeKPIs.map(k => KPI_CONFIG.find(c => c.key === k)?.label).join(', ')})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {visibleBatches.map(b => (
              <Card key={b.batch_id}
                className="border p-4 cursor-pointer hover:border-violet-300 hover:shadow-sm transition-all"
                onClick={() => setSelectedBatch(b)}
                data-testid={`overview-batch-card-${b.batch_id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-sm font-bold text-violet-600">#{b.batch_number}</span>
                    <span className="text-sm text-slate-600 ml-2">{b.course_name}</span>
                  </div>
                  <Badge variant="outline" className={`text-[9px] ${
                    b.status === 'Running' ? 'bg-emerald-50 text-emerald-700' :
                    b.status === 'Completed' ? 'bg-slate-100 text-slate-500' :
                    b.status === 'On Hold' ? 'bg-amber-50 text-amber-700' :
                    'bg-blue-50 text-blue-700'
                  }`}>{b.status}</Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-1.5">
                  <span className="font-medium text-slate-700">{b.trainer_name}</span>
                  <span>{b.centre_name}</span>
                  <span>{b.batch_type}</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-slate-400">{b.start_date}</span>
                  {b.timing && <span className="text-slate-400">{b.timing}</span>}
                  <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 ml-auto">{b.student_count} students</Badge>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeKPIs.length === 0 && (
        <Card className="border p-12 text-center">
          <Layers className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-400 font-medium">Click on a KPI card above to see batches</p>
          <p className="text-xs text-slate-300 mt-1">You can select multiple categories at once</p>
        </Card>
      )}

      {/* Batch Report Dialog */}
      <BatchReportDialog
        batch={selectedBatch}
        open={!!selectedBatch}
        onClose={() => setSelectedBatch(null)}
      />
    </div>
  );
}
